import React, { useCallback, useState } from "react";
import { Table, Button, Input, Alert, Tooltip } from "antd";
import NumberInput from "../common/NumberInput";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { DeleteOutlined } from "@ant-design/icons";
import { CarriagewayFootway } from "../../types/tender.types";
import { COLORS } from "../../utils/constants";
import { useTableRenderDeferral } from "../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../common/TableLoadingPlaceholder";

interface KerbsEdgingsSectionProps {
  carriagewayFootways: CarriagewayFootway[];
  onUpdate: (carriagewayFootways: CarriagewayFootway[]) => void;
  // Row selection is controlled by the parent so its tab-level "Delete
  // Selected" button can act on it.
  selectedRowKeys: React.Key[];
  onSelectedRowKeysChange: (keys: React.Key[]) => void;
  stickyOffset?: number;
  isViewMode?: boolean;
}

const ColTitle: React.FC<{ title: string }> = ({ title }) => {
  const unitMatch = title.match(/\s+(m[²³]?|Nr)$/);
  if (!unitMatch) {
    return (
      <Tooltip title={title}>
        <div className="ke-header-title">{title}</div>
      </Tooltip>
    );
  }
  const unit = unitMatch[1];
  const name = title.replace(/\s+(m[²³]?|Nr)$/, "");
  return (
    <div className="text-center">
      <Tooltip title={name}>
        <div className="ke-header-title">{name}</div>
      </Tooltip>
      <div className="ke-header-unit">{unit}</div>
    </div>
  );
};

const KerbsEdgingsSection: React.FC<KerbsEdgingsSectionProps> = ({
  carriagewayFootways,
  onUpdate,
  selectedRowKeys,
  onSelectedRowKeysChange,
  stickyOffset = 310,
  isViewMode = false,
}) => {
  // Defers this table's first mount by one paint cycle so a tender with a
  // lot of roads doesn't make this tab look frozen while the table's
  // initial render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Purely a perceived-lag fix:
  // once it flips false the table renders exactly as before.
  const isTableLoading = useTableRenderDeferral();

  const deleteRecords = useCallback(
    (ids: React.Key[]) => {
      const idSet = new Set(ids);
      onUpdate(carriagewayFootways.filter((item) => !idSet.has(item.id)));
    },
    [carriagewayFootways, onUpdate],
  );

  // Delete confirmation modal — matches the DeleteConfirmationModal pattern used
  // by Groundworks tables, rather than the antd Popconfirm previously used here.
  // idsToDelete is used for single-row delete; bulk delete is triggered by the
  // parent (its "Delete Selected" button lives at the tab level).
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState<React.Key[]>([]);
  const confirmDelete = () => {
    deleteRecords(idsToDelete);
    setDeleteModalVisible(false);
    setIdsToDelete([]);
    onSelectedRowKeysChange([]);
  };
  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setIdsToDelete([]);
  };

  const updateField = useCallback(
    (id: string, field: keyof CarriagewayFootway, value: string | number) => {
      onUpdate(
        carriagewayFootways.map((item) =>
          item.id === id ? { ...item, [field]: value } : item,
        ),
      );
    },
    [carriagewayFootways, onUpdate],
  );

  type TotalsKey =
    | "kerbRace" | "dowelBarsInRace" | "keyChannel" | "filletToCrossover"
    | "removeFillet" | "extrudedKerbRace" | "tempKerbBlock" | "removeTempKerbBlock"
    | "kerb10x5IncRace" | "kerb10x5OnRace" | "kerb6x5OnRace" | "dropKerb"
    | "delineationKerb" | "eoRadiusKerb" | "eoRadiusDropKerb" | "kasselKerb"
    | "triefKerb" | "beanyKerb" | "beanyGully" | "acoKerbDrain" | "acoKerbGully"
    | "eoRamps" | "edgings150x50" | "edgings200x50" | "tactilePaving" | "corduroyPaving";

  const totals = carriagewayFootways.reduce(
    (acc, item) => {
      (Object.keys(acc) as TotalsKey[]).forEach((k) => {
        acc[k] = acc[k] + ((item[k] as number) || 0);
      });
      return acc;
    },
    {
      kerbRace: 0, dowelBarsInRace: 0, keyChannel: 0, filletToCrossover: 0,
      removeFillet: 0, extrudedKerbRace: 0, tempKerbBlock: 0, removeTempKerbBlock: 0,
      kerb10x5IncRace: 0, kerb10x5OnRace: 0, kerb6x5OnRace: 0, dropKerb: 0,
      delineationKerb: 0, eoRadiusKerb: 0, eoRadiusDropKerb: 0, kasselKerb: 0,
      triefKerb: 0, beanyKerb: 0, beanyGully: 0, acoKerbDrain: 0, acoKerbGully: 0,
      eoRamps: 0,
      edgings150x50: 0, edgings200x50: 0, tactilePaving: 0, corduroyPaving: 0,
    } as Record<TotalsKey, number>,
  );

  const inputCol = (
    title: string,
    dataIndex: keyof CarriagewayFootway,
    width: number,
    precision = 3,
  ) => ({
    title: <ColTitle title={title} />,
    dataIndex,
    key: dataIndex as string,
    width,
    align: "right" as const,
    render: (value: number, record: CarriagewayFootway) => (
      <NumberInput
        value={value ?? 0}
        onChange={(val) => updateField(record.id, dataIndex, val)}
        decimalPlaces={precision}
        isInteger={precision === 0}
        style={{ width: "100%", textAlign: "right" }}
        size="small"
      />
    ),
  });

  const columns = [
    {
      title: "Road Name",
      dataIndex: "roadName",
      key: "roadName",
      width: 120,
      fixed: "left" as const,
      render: (value: string, record: CarriagewayFootway) => (
        <Input
          value={value}
          onChange={(e) => updateField(record.id, "roadName", e.target.value)}
          placeholder="Road name"
          size="small"
        />
      ),
    },
    // ── Kerbs ──────────────────────────────────────────────────────────────
    inputCol("Kerb Race m", "kerbRace", 105),
    inputCol("Dowel Bars in Race Nr", "dowelBarsInRace", 145, 0),
    inputCol("Key Channel m", "keyChannel", 115),
    inputCol("Fillet to Crossover m", "filletToCrossover", 150),
    inputCol("Remove Fillet m", "removeFillet", 120),
    inputCol("Extruded Kerb Race m", "extrudedKerbRace", 145),
    inputCol("Temp Kerb / Block m", "tempKerbBlock", 135),
    inputCol("Remove Temp Kerb / Block m", "removeTempKerbBlock", 175),
    inputCol("10x5 Kerb (inc Race) All Sizes m", "kerb10x5IncRace", 200),
    inputCol("10x5 Kerb (on Race) m", "kerb10x5OnRace", 155),
    inputCol("6x5 Kerb (on Race) m", "kerb6x5OnRace", 145),
    inputCol("Drop Kerb Nr", "dropKerb", 105, 0),
    inputCol("Delineation Kerb m", "delineationKerb", 140),
    inputCol("EO Radius Kerb m", "eoRadiusKerb", 130),
    inputCol("EO Radius Drop Kerb m", "eoRadiusDropKerb", 155),
    inputCol("Kassel Kerb m", "kasselKerb", 115),
    inputCol("Trief Kerb m", "triefKerb", 105),
    inputCol("Beany Kerb m", "beanyKerb", 105),
    inputCol("Beany Gully Nr", "beanyGully", 115, 0),
    inputCol("ACO Kerb Drain m", "acoKerbDrain", 130),
    inputCol("ACO Kerb Gully Nr", "acoKerbGully", 130, 0),
    inputCol("EO Ramps Nr", "eoRamps", 105, 0),
    // ── Edgings ────────────────────────────────────────────────────────────
    inputCol("150x50 Edgings m", "edgings150x50", 135),
    inputCol("200x50 Edgings m", "edgings200x50", 135),
    inputCol("Tactile Paving Nr", "tactilePaving", 135, 0),
    inputCol("Corduroy Paving Nr", "corduroyPaving", 145, 0),
    {
      title: "Actions",
      key: "actions",
      width: 75,
      fixed: "right" as const,
      align: 'center' as const,
      render: (_: unknown, record: CarriagewayFootway) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => {
            setIdsToDelete([record.id]);
            setDeleteModalVisible(true);
          }}
        />
      ),
    },
  ];

  const summaryLeaves: Array<TotalsKey | "roadName" | "actions"> = [
    "roadName",
    "kerbRace", "dowelBarsInRace", "keyChannel", "filletToCrossover",
    "removeFillet", "extrudedKerbRace", "tempKerbBlock", "removeTempKerbBlock",
    "kerb10x5IncRace", "kerb10x5OnRace", "kerb6x5OnRace", "dropKerb",
    "delineationKerb", "eoRadiusKerb", "eoRadiusDropKerb",
    "kasselKerb", "triefKerb", "beanyKerb", "beanyGully",
    "acoKerbDrain", "acoKerbGully", "eoRamps",
    "edgings150x50", "edgings200x50", "tactilePaving", "corduroyPaving",
    "actions",
  ];

  const integerFields = new Set<string>([
    "dowelBarsInRace", "dropKerb", "beanyGully", "acoKerbGully",
    "eoRamps", "tactilePaving", "corduroyPaving",
  ]);

  return (
    <>
      {isTableLoading ? (
        <TableLoadingPlaceholder />
      ) : (
      <Table
        className="kerbs-edgings-table"
        columns={columns}
        dataSource={carriagewayFootways}
        rowKey="id"
        rowSelection={isViewMode ? undefined : {
          selectedRowKeys,
          onChange: onSelectedRowKeysChange,
        }}
        pagination={false}
        scroll={{ x: 3700 }}
        sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
        size="small"
        bordered
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}>
              {/* colSpan merges the row-selection checkbox column (when present) with Road Name,
                  since rowSelection inserts a checkbox column before all defined columns. */}
              <Table.Summary.Cell key="roadName" index={0} colSpan={isViewMode ? 1 : 2} align="center">
                <span style={{ color: "#000" }}>Totals</span>
              </Table.Summary.Cell>
              {summaryLeaves.filter((key) => key !== "roadName").map((key, idx) => {
                // Accounts for the checkbox column (when present) and the merged Totals cell above.
                const colIndex = idx + (isViewMode ? 1 : 2);
                if (key === "actions") {
                  return <Table.Summary.Cell key="actions" index={colIndex} />;
                }
                const val = (totals[key as TotalsKey] as number) || 0;
                // Matches inputCol's default decimalPlaces=3 for non-integer fields
                // so the Totals row shows the same decimal precision as its cells.
                const precision = integerFields.has(key) ? 0 : 3;
                return (
                  <Table.Summary.Cell key={key} index={colIndex} align="right">
                    <span style={{ color: "#000", paddingRight: 8 }}>
                      {val.toFixed(precision)}
                    </span>
                  </Table.Summary.Cell>
                );
              })}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      )}

      {carriagewayFootways.length === 0 && (
        <Alert
          message="No records. Click 'Add Road' to get started."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      <style>{`
        .kerbs-edgings-table {
          font-size: 14px;
        }
        .kerbs-edgings-table .ant-table {
          border: none !important;
        }
        .kerbs-edgings-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-radius: 0 !important;
        }
        .kerbs-edgings-table .ant-table-thead > tr > th {
          min-width: 100px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 10px 6px 8px 6px !important;
          text-align: center !important;
          font-size: 12px !important;
          vertical-align: top !important;
        }
        .kerbs-edgings-table .ant-table-thead > tr > th .ke-header-title {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          display: block !important;
          width: 100% !important;
        }
        .kerbs-edgings-table .ant-table-thead > tr > th .ke-header-unit {
          font-size: 11px !important;
          color: #6b7280 !important;
          font-weight: 500 !important;
        }
        .kerbs-edgings-table .ant-table-thead > tr > th.ant-table-cell-fix-left,
        .kerbs-edgings-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
          background-color: ${COLORS.tableHeaderBg} !important;
          z-index: 100 !important;
        }
        .kerbs-edgings-table .ant-table-tbody > tr > td.ant-table-cell-fix-left {
          background-color: #fff !important;
        }
        .kerbs-edgings-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
          background-color: #fafafa !important;
        }

        /* Bulk-select checkboxes should not highlight the row */
        .kerbs-edgings-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }
        .kerbs-edgings-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
          background-color: #fafafa !important;
        }

        /* The merged Totals cell (checkbox + Road Name) is fixed-left, so the
           tbody fix-left override above would otherwise paint it white instead
           of matching the rest of the totals row. */
        .kerbs-edgings-table .ant-table-summary td.ant-table-cell-fix-left {
          background-color: #FFF7E6 !important;
        }
      `}</style>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={idsToDelete.length > 1 ? `Are you sure you want to delete ${idsToDelete.length} records?` : undefined}
      />
    </>
  );
};

export default KerbsEdgingsSection;
